import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { Container, Row, Col, Card, ListGroup, Form, Button, Badge, Spinner, InputGroup } from "react-bootstrap";
import { GlobeAmericas, CheckCircleFill, Save2, Search, FilterSquare } from "react-bootstrap-icons";

export default function LocalesManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data State
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState({ projects: [], content_types: [], formats: [] });
  
  // Selection State
  const [activeType, setActiveType] = useState("projects"); // projects | content_types | formats
  const [selectedItem, setSelectedItem] = useState(null);
  const [allowedIds, setAllowedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, []);

  async function fetchInitialData() {
    setLoading(true);
    const [langs, projs, types, forms] = await Promise.all([
      supabase.from("languages").select("*").order("name"),
      supabase.from("projects").select("id, name, allowed_languages"),
      supabase.from("content_types").select("id, name, allowed_languages"),
      supabase.from("formats").select("id, name, allowed_languages")
    ]);

    setLanguages(langs.data || []);
    setCategories({
      projects: projs.data || [],
      content_types: types.data || [],
      formats: forms.data || []
    });
    setLoading(false);
  }

  // When a specific Project/Type/Format is clicked
  const handleSelectCategoryItem = (item) => {
    setSelectedItem(item);
    setAllowedIds(item.allowed_languages || []);
  };

  const toggleLanguage = (langId) => {
    setAllowedIds(prev => 
      prev.includes(langId) ? prev.filter(id => id !== langId) : [...prev, langId]
    );
  };

  const handleSave = async () => {
    if (!selectedItem) return;
    setSaving(true);

    const { error } = await supabase
      .from(activeType)
      .update({ allowed_languages: allowedIds })
      .eq("id", selectedItem.id);

    if (error) {
      alert("Error saving: " + error.message);
    } else {
      // Update local state so UI reflects the change
      setCategories(prev => ({
        ...prev,
        [activeType]: prev[activeType].map(item => 
          item.id === selectedItem.id ? { ...item, allowed_languages: allowedIds } : item
        )
      }));
      alert("Settings updated successfully!");
    }
    setSaving(false);
  };

  const filteredLanguages = languages.filter(l => 
    l.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    l.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <Container fluid className="py-4" style={{ maxWidth: '1600px' }}>
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h2 className="fw-bold m-0"><GlobeAmericas className="me-2 text-primary"/>Locales Management</h2>
          <p className="text-muted">Configure language restrictions for Projects, Content Types, and Formats.</p>
        </div>
        {selectedItem && (
          <Button variant="success" size="lg" className="px-5 shadow" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm"/> : <><Save2 className="me-2"/> Save Configuration</>}
          </Button>
        )}
      </div>

      <Row className="g-4">
        {/* LEFT COLUMN: The "Target" Selector */}
        <Col md={4} lg={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white py-3 border-0">
              <Form.Select 
                className="fw-bold" 
                value={activeType} 
                onChange={(e) => { 
                  setActiveType(e.target.value); 
                  setSelectedItem(null); 
                  setAllowedIds([]); 
                }}
              >
                <option value="projects">Projects</option>
                <option value="content_types">Content Types</option>
                <option value="formats">Formats</option>
              </Form.Select>
            </Card.Header>
            <ListGroup variant="flush" className="overflow-auto" style={{ maxHeight: '70vh' }}>
              {categories[activeType].map(item => (
                <ListGroup.Item 
                  key={item.id} 
                  action 
                  active={selectedItem?.id === item.id}
                  onClick={() => handleSelectCategoryItem(item)}
                  className="d-flex justify-content-between align-items-center py-3"
                >
                  <span className="fw-bold">{item.name}</span>
                  <Badge bg={item.allowed_languages?.length ? "primary" : "secondary"} pill>
                    {item.allowed_languages?.length || 0}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        </Col>

        {/* RIGHT COLUMN: The Toggle Grid */}
        <Col md={8} lg={9}>
          <Card className="shadow-sm border-0 h-100 min-vh-75">
            <Card.Body>
              {!selectedItem ? (
                <div className="text-center py-5 mt-5">
                  <div className="display-1 text-light mb-3"><FilterSquare /></div>
                  <h4 className="text-muted">Select a {activeType.slice(0, -1)} to manage locales</h4>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="fw-bold text-dark m-0">
                      Allowed for: <span className="text-primary">{selectedItem.name}</span>
                    </h4>
                    <div className="d-flex gap-2">
                      <Button variant="outline-secondary" size="sm" onClick={() => setAllowedIds(languages.map(l => l.id))}>Select All</Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => setAllowedIds([])}>Clear All</Button>
                    </div>
                  </div>

                  <InputGroup className="mb-4 shadow-sm">
                    <InputGroup.Text className="bg-white border-end-0"><Search size={14}/></InputGroup.Text>
                    <Form.Control 
                      className="border-start-0" 
                      placeholder="Search languages..." 
                      value={searchTerm} 
                      onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                  </InputGroup>

                  <Row className="g-3 overflow-auto" style={{ maxHeight: '60vh' }}>
                    {filteredLanguages.map(lang => {
                      const isSelected = allowedIds.includes(lang.id);
                      return (
                        <Col sm={6} md={4} lg={3} key={lang.id}>
                          <div 
                            className={`p-3 border rounded-3 cursor-pointer d-flex align-items-center transition-all ${isSelected ? 'border-primary bg-primary bg-opacity-10' : 'bg-light'}`}
                            onClick={() => toggleLanguage(lang.id)}
                            style={{ cursor: 'pointer', transition: '0.2s' }}
                          >
                            <div className="fs-3 me-3">{lang.flag}</div>
                            <div className="flex-grow-1 overflow-hidden">
                              <div className={`fw-bold small text-truncate ${isSelected ? 'text-primary' : 'text-dark'}`}>{lang.name}</div>
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>{lang.code || lang.id.slice(0,5)}</div>
                            </div>
                            {isSelected && <CheckCircleFill className="text-primary ms-2" />}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}